import apiClient from './api';

// Note: Google OAuth configuration is handled server-side for security
// No client-side credentials needed

export interface GA4Account {
  accountId: string;
  accountName: string;
  createTime?: string;
  updateTime?: string;
  properties: GA4Property[];
}

export interface GA4Property {
  propertyId: string;
  propertyName: string;
  websiteUrl?: string;
  industryCategory?: string;
  timeZone?: string;
  createTime?: string;
  updateTime?: string;
}

export interface GA4Connection {
  id: string;
  keyName: string;
  accountId: string;
  propertyId: string;
  propertyName: string;
  connectedAt: string;
  createdAt: string;
}

class GoogleAnalyticsService {
  /**
   * Initiate Google OAuth flow via backend
   */
  async initiateOAuth(): Promise<{ success: boolean; authUrl?: string; error?: string }> {
    try {
      const response = await apiClient.get('/oauth/google/url');
      
      if (response.data.success) {
        // Redirect to Google OAuth URL
        window.location.href = response.data.authUrl;
        return { success: true, authUrl: response.data.authUrl };
      } else {
        return { success: false, error: response.data.error || 'Failed to generate OAuth URL' };
      }
    } catch (error: any) {
      console.error('Error initiating OAuth:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || error.message || 'Failed to initiate OAuth' 
      };
    }
  }

  /**
   * Handle OAuth callback via backend
   */
  async handleOAuthCallback(code: string, state: string): Promise<{ 
    success: boolean; 
    error?: string;
    autoConnected?: boolean;
    connectionData?: {
      apiKeyId: string;
      connectionId: string;
      projectId: string;
      propertyId: string;
      propertyName: string;
      autoConnected?: boolean;
    };
  }> {
    try {
      console.log('Handling OAuth callback with code and state', { codeLength: code.length, stateLength: state.length });
      
      const response = await apiClient.post('/oauth/google/callback', {
        code,
        state
      });

      if (response.data.success) {
        return { 
          success: true,
          autoConnected: response.data.autoConnected || false,
          connectionData: response.data.connectionData || null
        };
      } else {
        return { 
          success: false, 
          error: response.data.error || 'OAuth authentication failed' 
        };
      }
    } catch (error: any) {
      console.error('OAuth callback error:', error);
      
      // Extract error message from response if available
      const errorMessage = error.response?.data?.error || 
                          error.message || 
                          'OAuth authentication failed';
      
      return { 
        success: false, 
        error: errorMessage
      };
    }
  }

  /**
   * Get user's Google Analytics accounts via backend
   */
  async getAccounts(): Promise<GA4Account[]> {
    try {
      const response = await apiClient.get('/oauth/google/accounts');
      
      if (response.data.success) {
        return response.data.accounts || [];
      } else {
        throw new Error(response.data.error || 'Failed to fetch GA4 accounts');
      }
    } catch (error) {
      console.error('Error fetching GA4 accounts:', error);
      throw error;
    }
  }

  /**
   * Connect a specific GA4 property via backend
   */
  async connectProperty(
    accountId: string, 
    propertyId: string, 
    propertyName?: string,
    projectId?: string
  ): Promise<{ 
    success: boolean; 
    error?: string; 
    data?: {
      apiKeyId: string;
      connectionId?: string;
      projectId?: string;
      propertyId: string;
      propertyName?: string;
    }
  }> {
    try {
      const response = await apiClient.post('/oauth/google/connect-property', {
        accountId,
        propertyId,
        propertyName,
        projectId
      });

      if (response.data.success) {
        return { 
          success: true,
          data: response.data.data
        };
      } else {
        return { 
          success: false, 
          error: response.data.error || 'Failed to connect property' 
        };
      }
    } catch (error: any) {
      console.error('Error connecting property:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || error.message || 'Failed to connect property' 
      };
    }
  }

  /**
   * Switch GA4 property for a project (replaces existing connection)
   */
  async switchProperty(
    accountId: string, 
    propertyId: string, 
    propertyName: string,
    projectId: string
  ): Promise<{ 
    success: boolean; 
    error?: string; 
    data?: {
      apiKeyId: string;
      connectionId: string;
      projectId: string;
      oldPropertyId?: string;
      newPropertyId: string;
      propertyName: string;
    }
  }> {
    try {
      const response = await apiClient.post('/oauth/google/switch-property', {
        accountId,
        propertyId,
        propertyName,
        projectId
      });

      if (response.data.success) {
        return { 
          success: true,
          data: response.data.data
        };
      } else {
        return { 
          success: false, 
          error: response.data.error || 'Failed to switch property' 
        };
      }
    } catch (error: any) {
      console.error('Error switching property:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || error.message || 'Failed to switch property' 
      };
    }
  }

  /**
   * Check if user has any GA4 connections
   */
  async hasConnections(): Promise<boolean> {
    try {
      const response = await apiClient.get('/oauth/google/connections');
      
      if (response.data.success) {
        return response.data.hasConnections || false;
      } else {
        console.error('Error checking connections:', response.data.error);
        return false;
      }
    } catch (error) {
      console.error('Error checking connections:', error);
      return false;
    }
  }

  /**
   * Get user's GA4 connections
   */
  async getConnections(): Promise<GA4Connection[]> {
    try {
      const response = await apiClient.get('/oauth/google/connections');
      
      if (response.data.success) {
        return response.data.connections || [];
      } else {
        throw new Error(response.data.error || 'Failed to fetch connections');
      }
    } catch (error) {
      console.error('Error fetching connections:', error);
      throw error;
    }
  }

  /**
   * Disconnect GA4 integration
   */
  async disconnect(): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await apiClient.delete('/oauth/google/disconnect');

      if (response.data.success) {
        return { success: true };
      } else {
        return { 
          success: false, 
          error: response.data.error || 'Failed to disconnect' 
        };
      }
    } catch (error: any) {
      console.error('Error disconnecting GA4:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || error.message || 'Failed to disconnect' 
      };
    }
  }

  /**
   * Generate random state for OAuth security (used by callback handler)
   */
  generateState(): string {
    return Math.random().toString(36).substring(2, 15) + 
           Math.random().toString(36).substring(2, 15);
  }

  /**
   * Complete onboarding after GA4 connection
   */
  async completeOnboarding(projectId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await apiClient.post('/oauth/google/complete-onboarding', {
        projectId
      });

      if (response.data.success) {
        return { success: true };
      } else {
        return { 
          success: false, 
          error: response.data.error || 'Failed to complete onboarding' 
        };
      }
    } catch (error: any) {
      console.error('Error completing onboarding:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || error.message || 'Failed to complete onboarding' 
      };
    }
  }

  /**
   * Auto-connect GA4 property for onboarding
   */
  async autoConnect(projectId?: string): Promise<{ 
    success: boolean; 
    error?: string; 
    data?: {
      apiKeyId: string;
      connectionId: string;
      projectId: string;
      propertyId: string;
      propertyName: string;
      alreadyConnected?: boolean;
    }
  }> {
    try {
      const response = await apiClient.post('/oauth/google/auto-connect', {
        projectId
      });

      if (response.data.success) {
        return { 
          success: true,
          data: response.data.data
        };
      } else {
        return { 
          success: false, 
          error: response.data.error || 'Failed to auto-connect GA4 property' 
        };
      }
    } catch (error: any) {
      console.error('Error auto-connecting GA4 property:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || error.message || 'Failed to auto-connect GA4 property' 
      };
    }
  }

  /**
   * Refresh GA4 metadata for a property
   */
  async refreshMetadata(propertyId: string): Promise<{ 
    success: boolean; 
    error?: string; 
    message?: string;
  }> {
    try {
      const response = await apiClient.post(`/oauth/google/refresh-metadata/${propertyId}`);

      if (response.data.success) {
        return { 
          success: true,
          message: response.data.message
        };
      } else {
        return { 
          success: false, 
          error: response.data.error || 'Failed to refresh metadata' 
        };
      }
    } catch (error: any) {
      console.error('Error refreshing metadata:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || error.message || 'Failed to refresh metadata' 
      };
    }
  }
}

export const googleAnalyticsService = new GoogleAnalyticsService();